import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/common/Footer';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 max-w-3xl mx-auto px-4 py-16">
        <h1 className="text-3xl font-bold text-foreground mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-2">Last updated: April 2026</p>
        <p className="text-sm text-muted-foreground mb-10">
          CTX is committed to protecting your privacy. This Privacy Policy explains what data we
          collect, why we collect it, how we use it, and your rights in relation to it. Please
          read this policy carefully alongside our{' '}
          <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>{' '}
          and{' '}
          <Link to="/cookie-policy" className="text-primary hover:underline">Cookie Policy</Link>.
        </p>

        <div className="space-y-8 text-sm text-muted-foreground leading-relaxed">

          {/* 1 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">1. Who We Are</h2>
            <p>
              Cerebral Trading Labs ("CTX", "we", "us", "our") operates the CTX trading journal
              platform. For the purposes of applicable data protection law, CTX is the data
              controller responsible for your personal information.
            </p>
            <p className="mt-3">
              Our contact details for data-related enquiries are set out at the end of this policy.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">2. Information We Collect</h2>
            <p className="mb-3">
              We collect the following categories of information when you use the Service:
            </p>

            <h3 className="font-semibold text-foreground mb-2">a) Account Information</h3>
            <p>
              When you register, we collect your email address, display name, and (if you use
              social sign-in) limited profile information provided by your identity provider. We
              use this to create and manage your account.
            </p>

            <h3 className="font-semibold text-foreground mt-4 mb-2">b) Trading & Journal Data</h3>
            <p>
              All trade records, journal entries, notes, playbook rules, goals, and other content
              you voluntarily enter into the platform ("User Content") are stored to provide the
              core functionality of the Service. This data belongs to you — we do not use it for
              any purpose other than operating the Service for you.
            </p>

            <h3 className="font-semibold text-foreground mt-4 mb-2">c) AI Conversation Data</h3>
            <p>
              Messages and trading context exchanged with AI features (Sage AI Coach, Insight Lab,
              Journal.AI) are processed to generate responses. See Section 5 (AI Processing) for
              important details.
            </p>

            <h3 className="font-semibold text-foreground mt-4 mb-2">d) Usage & Analytics Data</h3>
            <p>
              We collect information about how you interact with the Service — pages visited,
              features used, session duration, and interaction events. This data is collected in
              aggregate form using an analytics tool and is used solely to improve the product.
            </p>

            <h3 className="font-semibold text-foreground mt-4 mb-2">e) Payment Information</h3>
            <p>
              Subscription and billing data is processed directly by our payment processor. We
              receive only a non-sensitive summary (subscription tier, billing status, last-4 of
              card). We do not store full payment card numbers.
            </p>

            <h3 className="font-semibold text-foreground mt-4 mb-2">f) Technical Data</h3>
            <p>
              When you access the Service, we may automatically collect your IP address, browser
              type, device type, operating system, and approximate geographic region. This is used
              for security, fraud prevention, and service optimisation.
            </p>

            <h3 className="font-semibold text-foreground mt-4 mb-2">g) Communications</h3>
            <p>
              If you contact us for support or send us an email, we retain those communications to
              respond to your enquiry and improve our support processes.
            </p>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">
              3. Legal Basis for Processing (GDPR)
            </h2>
            <p>
              If you are located in the European Economic Area (EEA), the United Kingdom, or
              another jurisdiction where GDPR or equivalent law applies, we process your personal
              data under one or more of the following legal bases:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                <strong className="text-foreground">Contract:</strong> Processing necessary to
                perform the contract between you and CTX (i.e., providing the Service you
                subscribed to).
              </li>
              <li>
                <strong className="text-foreground">Legitimate Interests:</strong> Processing
                necessary for our legitimate business interests, such as preventing fraud,
                improving the Service, and ensuring security, provided those interests are not
                overridden by your rights.
              </li>
              <li>
                <strong className="text-foreground">Legal Obligation:</strong> Processing required
                to comply with applicable laws, such as financial record-keeping requirements.
              </li>
              <li>
                <strong className="text-foreground">Consent:</strong> Where we rely on your
                consent (e.g., optional analytics cookies), you may withdraw that consent at any
                time without affecting the lawfulness of processing before withdrawal.
              </li>
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">4. How We Use Your Data</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>To create and manage your account and authenticate your access.</li>
              <li>To provide, operate, and maintain the Service and all its features.</li>
              <li>
                To generate personalised analytics, psychology insights, and AI-powered coaching
                responses based on your trading data.
              </li>
              <li>To process your subscription payments and manage billing.</li>
              <li>
                To send transactional communications: account confirmation, password reset, billing
                receipts, and material service updates. We do not send marketing emails without
                your explicit consent.
              </li>
              <li>
                To monitor for security threats, abuse, and violations of our Terms of Service.
              </li>
              <li>
                To analyse aggregated usage patterns and improve the quality and performance of the
                Service.
              </li>
              <li>
                To comply with our legal obligations and enforce our Terms of Service.
              </li>
            </ul>
          </section>

          {/* 5 — AI Processing */}
          <section className="border border-border rounded-lg p-5 bg-white/[0.03]">
            <h2 className="text-lg font-semibold text-foreground mb-3">5. AI Processing Notice</h2>
            <p className="font-medium text-foreground mb-3">
              When you use AI-powered features, relevant portions of your User Content (trade data,
              journal notes, chat messages) are transmitted to a third-party AI language model
              provider to generate responses.
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                This provider processes your data solely to generate the requested response and
                does not use your data to train its models by default under our agreement.
              </li>
              <li>
                Only the data required to generate a response is sent — we do not transmit your
                full data history unless it is needed for context in that specific interaction.
              </li>
              <li>
                You can limit AI data exposure by avoiding the entry of personally identifiable
                information in trade notes or journal entries beyond what is needed for your
                journaling purposes.
              </li>
              <li>
                AI conversations are retained in our database to provide session continuity. You
                can request deletion of AI conversation history at any time by contacting us.
              </li>
            </ul>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">
              6. Service Providers & Sub-Processors
            </h2>
            <p>
              We work with a limited number of trusted third-party companies to operate the
              Service. Each provider has contractual obligations to keep your data secure and to
              process it only as directed by us. We do not allow our service providers to use your
              data for their own independent purposes.
            </p>
            <p className="mt-3">Our current categories of service providers include:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                <strong className="text-foreground">Payment Processor (Stripe):</strong> Handles
                subscription billing and payment card processing. Your payment data is governed by
                Stripe's Privacy Policy. We receive only non-sensitive billing metadata.
              </li>
              <li>
                <strong className="text-foreground">Cloud Database & Authentication Provider:</strong>{' '}
                Stores your account information, trading data, and journal entries securely. Data
                is encrypted at rest and in transit.
              </li>
              <li>
                <strong className="text-foreground">AI Language Model Provider:</strong> Processes
                User Content submitted to AI features to generate responses. See Section 5.
              </li>
              <li>
                <strong className="text-foreground">Analytics Provider:</strong> Collects
                anonymised usage data (page views, feature interactions) to help us understand how
                the Service is used. This provider may set cookies or use similar tracking
                technologies — see our Cookie Policy for details.
              </li>
              <li>
                <strong className="text-foreground">Transactional Email Provider:</strong> Sends
                automated emails on our behalf (account verification, password reset, billing
                notifications). Email content is not shared with other parties.
              </li>
              <li>
                <strong className="text-foreground">Cloud Hosting & Infrastructure:</strong> Our
                application servers and static assets are hosted on cloud infrastructure providers.
                These providers do not have independent access to your personal data.
              </li>
            </ul>
            <p className="mt-3">
              We do not engage any additional sub-processors without taking steps to ensure they
              meet appropriate data protection standards.
            </p>
          </section>

          {/* 7 — prominent */}
          <section className="border border-border rounded-lg p-5 bg-white/[0.03]">
            <h2 className="text-lg font-semibold text-foreground mb-3">
              7. We Do Not Sell Your Data
            </h2>
            <p className="font-medium text-foreground">
              CTX does not sell, rent, lease, or trade your personal information or your trading
              data to any third party for any purpose — including advertising, lead generation, or
              data brokerage.
            </p>
            <p className="mt-3">
              Your trading data is your proprietary information. It is used exclusively to operate
              the Service for you. We derive no revenue from data sales and have no intention of
              doing so.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">8. Data Retention</h2>
            <p>
              We retain your personal data and User Content for as long as your account is active
              or as needed to provide the Service. If you delete your account, we will permanently
              delete your data from our systems within 30 days, except where:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                Retention is required by applicable law (e.g., tax and financial record-keeping
                obligations), in which case only the minimum data required by law is retained for
                the legally mandated period.
              </li>
              <li>
                Data is needed to resolve outstanding disputes or enforce our agreements.
              </li>
              <li>
                Data exists in system backups, which are purged on a rolling schedule (typically
                within 90 days of account deletion).
              </li>
            </ul>
            <p className="mt-3">
              Analytics data collected in aggregated, non-identifiable form may be retained
              indefinitely for product improvement purposes.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">9. Security</h2>
            <p>
              We implement industry-standard technical and organisational security measures to
              protect your data, including:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Encryption of data in transit (TLS/HTTPS) and at rest.</li>
              <li>JWT-based authentication with short-lived access tokens and automatic refresh.</li>
              <li>Row-level security policies so users can only access their own data.</li>
              <li>Access controls limiting internal access to personal data to those with a legitimate need.</li>
            </ul>
            <p className="mt-3">
              However, no method of transmission over the internet or electronic storage is 100%
              secure. We cannot guarantee absolute security. If you believe your account has been
              compromised, please contact us immediately.
            </p>
            <p className="mt-3">
              In the event of a data breach that is likely to result in risk to your rights and
              freedoms, we will notify you and, where required by law, the relevant supervisory
              authority, within the timeframes required by applicable law.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">
              10. International Data Transfers
            </h2>
            <p>
              CTX operates globally. Your data may be processed in countries outside your country
              of residence, including countries that may not provide the same level of data
              protection as your home jurisdiction.
            </p>
            <p className="mt-3">
              Where we transfer personal data from the EEA or UK to third countries, we rely on
              appropriate transfer mechanisms such as the EU Standard Contractual Clauses (SCCs) or
              the UK International Data Transfer Agreement (IDTA), or we transfer to countries
              recognised as providing an adequate level of protection.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">
              11. Your Rights — EEA, UK & Other Jurisdictions
            </h2>
            <p>
              Depending on your location, you may have the following rights in relation to your
              personal data:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                <strong className="text-foreground">Right of Access:</strong> Request a copy of
                the personal data we hold about you.
              </li>
              <li>
                <strong className="text-foreground">Right to Rectification:</strong> Request
                correction of inaccurate or incomplete data.
              </li>
              <li>
                <strong className="text-foreground">Right to Erasure ("Right to be Forgotten"):</strong>{' '}
                Request deletion of your personal data, subject to applicable legal retention
                requirements.
              </li>
              <li>
                <strong className="text-foreground">Right to Data Portability:</strong> Receive
                your data in a commonly used, machine-readable format (e.g., CSV export of your
                trade history).
              </li>
              <li>
                <strong className="text-foreground">Right to Restrict Processing:</strong> Request
                that we limit how we use your data in certain circumstances.
              </li>
              <li>
                <strong className="text-foreground">Right to Object:</strong> Object to processing
                based on legitimate interests or for direct marketing purposes.
              </li>
              <li>
                <strong className="text-foreground">Rights Related to Automated Decision-Making:</strong>{' '}
                We do not make solely automated decisions that produce legal or similarly
                significant effects on you. Our AI features provide suggestions only — all
                consequential decisions are made by you.
              </li>
              <li>
                <strong className="text-foreground">Right to Withdraw Consent:</strong> Where
                processing is based on your consent, you may withdraw it at any time without
                affecting the lawfulness of prior processing.
              </li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, please contact us at the details in Section 16. We
              will respond within 30 days (or within the timeframe required by applicable law).
              We may need to verify your identity before processing your request.
            </p>
            <p className="mt-3">
              You also have the right to lodge a complaint with your local data protection
              authority if you believe we have not handled your data in accordance with applicable law.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">
              12. California Privacy Rights (CCPA / CPRA)
            </h2>
            <p>
              If you are a California resident, the California Consumer Privacy Act (CCPA) and
              California Privacy Rights Act (CPRA) provide you with specific rights regarding your
              personal information:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                <strong className="text-foreground">Right to Know:</strong> You may request
                disclosure of the categories and specific pieces of personal information we have
                collected about you, the categories of sources, our business purposes for
                collection, and the categories of third parties with whom we share it.
              </li>
              <li>
                <strong className="text-foreground">Right to Delete:</strong> You may request
                deletion of personal information we have collected from you, subject to certain
                exceptions.
              </li>
              <li>
                <strong className="text-foreground">Right to Correct:</strong> You may request
                correction of inaccurate personal information we maintain about you.
              </li>
              <li>
                <strong className="text-foreground">Right to Opt-Out of Sale or Sharing:</strong>{' '}
                We do not sell or share your personal information as defined under the CCPA/CPRA.
                You do not need to opt out.
              </li>
              <li>
                <strong className="text-foreground">Right to Non-Discrimination:</strong> We will
                not discriminate against you for exercising any of your CCPA/CPRA rights.
              </li>
            </ul>
            <p className="mt-3">
              To submit a CCPA/CPRA request, contact us at the details in Section 16.
            </p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">13. Children's Privacy</h2>
            <p>
              The Service is intended solely for users who are 18 years of age or older. We do not
              knowingly collect personal information from anyone under the age of 18. If you
              believe we have inadvertently collected information from a minor, please contact us
              immediately and we will take steps to delete that information promptly.
            </p>
          </section>

          {/* 14 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">14. Cookies & Tracking</h2>
            <p>
              We use cookies and similar technologies for authentication, session management, and
              (with your consent) analytics. For full details on the cookies we use and how to
              manage them, see our{' '}
              <Link to="/cookie-policy" className="text-primary hover:underline">
                Cookie Policy
              </Link>.
            </p>
          </section>

          {/* 15 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">
              15. Changes to This Privacy Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our
              practices, technology, legal requirements, or for other operational reasons. When we
              make material changes, we will notify you by email and/or by posting a prominent
              notice within the Service, at least 14 days before the changes take effect.
            </p>
            <p className="mt-3">
              Your continued use of the Service after the effective date of any update constitutes
              your acceptance of the updated Policy. If you do not agree with the changes, you
              must stop using the Service.
            </p>
          </section>

          {/* 16 */}
          <section>
            <h2 className="text-lg font-semibold text-foreground mb-3">16. Contact Us</h2>
            <p>
              For any questions, concerns, or requests relating to this Privacy Policy or your
              personal data, please contact our privacy team:
            </p>
            <p className="mt-2">
              <strong className="text-foreground">Cerebral Trading Labs — Privacy</strong><br />
              Email: support@ctxtraders.com<br />
              Ontario, Canada
            </p>
            <p className="mt-3">
              We aim to respond to all legitimate requests within 30 days. Occasionally it may
              take longer if your request is complex or you have made multiple requests — in that
              case, we will keep you informed.
            </p>
          </section>

        </div>

        <div className="mt-12 pt-8 border-t border-border text-xs text-muted-foreground">
          <p>
            This Privacy Policy is provided as a draft for informational purposes and should be
            reviewed by qualified legal counsel before being treated as final. It is not legal advice.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
